import { useState, useEffect, useMemo } from 'react'
import { PurchaseRequest } from '../types'
import { useAuth } from '../hooks/useAuth'
import { useDeepLinkById } from '../hooks/useDeepLinkById'
import { useAlert } from '../contexts/AlertContext'
import { 
  loadPurchaseRequests, 
  acceptPurchaseRequestByReferent,
  orderPurchaseRequestByReferent,
  completePurchaseRequestByReferent,
  rejectPurchaseRequestByReferent
} from '../lib/api'
import PurchaseRequestsTable from '../components/PurchaseRequestsTable'
import PurchaseRequestCard from '../components/PurchaseRequestCard'
import PurchaseRequestPrintView from '../components/PurchaseRequestPrintView'
import Pagination from '../components/Pagination'
import { useIsMobile } from '../hooks/useMediaQuery'
import { DetailsModal } from '../components/DetailsModal'
import { protectedFileHref } from '../utils/fileDownload'
import { CancelRequestModal } from '../components/CancelRequestModal'
import './ReferentView.css'
import '../styles/common-buttons.css'

interface ReferentViewProps {
  purchaseRole?: string
}

function ReferentView({ purchaseRole = 'referent' }: ReferentViewProps) {
  const { user } = useAuth()
  const { showAlert } = useAlert()
  const effectivePurchaseRole = purchaseRole || 'referent'
  const [requests, setRequests] = useState<PurchaseRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards')
  const [selectedRequest, setSelectedRequest] = useState<PurchaseRequest | null>(null)
  const [showCompleteModal, setShowCompleteModal] = useState(false)
  const [showCardModal, setShowCardModal] = useState(false)
  const [completionComment, setCompletionComment] = useState('')
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const [printRequest, setPrintRequest] = useState<PurchaseRequest | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [searchText, setSearchText] = useState('')
  const [sectionPages, setSectionPages] = useState<Record<string, number>>({})
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectModalRequest, setRejectModalRequest] = useState<PurchaseRequest | null>(null)
  const isMobile = useIsMobile()
  const itemsPerPage = isMobile ? 20 : 100

  useEffect(() => {
    if (user) {
      loadRequestsData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, purchaseRole])

  const loadRequestsData = async () => {
    if (!user) return
    
    try {
      setLoading(true)
      const effectiveRole = purchaseRole || user.role
      const data = await loadPurchaseRequests(user.id, user.role ?? null, effectiveRole)
      setRequests(data)
    } catch (error) {
      console.error('Chyba při načítání žádostí:', error)
      alert('Chyba při načítání žádostí o nákup')
    } finally {
      setLoading(false)
    }
  }

  useDeepLinkById({
    items: requests,
    ready: !loading,
    onFound: (r) => {
      setStatusFilter(r.status)
      setSelectedRequest(r)
      setShowCardModal(true)
    },
    onNotFound: () => showAlert('Žádost nenalezena nebo již byla zpracována.', 'error'),
    elementIdFor: (id) => `purchase-${id}`,
  })

  const handleAccept = async (requestId: string) => {
    try {
      await acceptPurchaseRequestByReferent(requestId, effectivePurchaseRole)
      await loadRequestsData()
      alert('Žádost byla přijata')
    } catch (error) {
      console.error('Chyba při přijetí žádosti:', error)
      alert('Chyba při přijetí žádosti')
    }
  }

  const handleOrder = async (requestId: string) => {
    try {
      await orderPurchaseRequestByReferent(requestId, effectivePurchaseRole)
      await loadRequestsData()
      alert('Žádost byla označena jako objednaná')
    } catch (error) {
      console.error('Chyba při označení jako objednaná:', error)
      alert('Chyba při označení jako objednaná')
    }
  }

  const handleComplete = async () => {
    if (!selectedRequest) return

    try {
      await completePurchaseRequestByReferent(
        selectedRequest.id,
        completionComment || undefined,
        effectivePurchaseRole
      )
      setShowCompleteModal(false)
      setSelectedRequest(null)
      setCompletionComment('')
      await loadRequestsData()
      alert('Žádost byla dokončena')
    } catch (error) {
      console.error('Chyba při dokončení žádosti:', error)
      alert('Chyba při dokončení žádosti')
    }
  }

  const openRejectModal = (request: PurchaseRequest) => {
    setRejectModalRequest(request)
    setShowRejectModal(true)
  }

  const handleRejectReferent = async (reason: string) => {
    if (!rejectModalRequest) return

    try {
      await rejectPurchaseRequestByReferent(rejectModalRequest.id, reason, effectivePurchaseRole)
      setShowRejectModal(false)
      setRejectModalRequest(null)
      if (selectedRequest?.id === rejectModalRequest.id) {
        setShowCardModal(false)
        setSelectedRequest(null)
      }
      await loadRequestsData()
      alert('Žádost byla zamítnuta')
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Chyba při zamítnutí žádosti'
      console.error('Chyba při zamítnutí žádosti:', error)
      alert(message)
    }
  }

  const renderRejectButton = (request: PurchaseRequest) => (
    <button
      className="btn-reject"
      onClick={(e) => {
        e.stopPropagation()
        openRejectModal(request)
      }}
    >
      Zamítnout
    </button>
  )

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      'new': 'Nová',
      'deferred': 'Odloženo ekonomem',
      'approved_by_economist': 'Schváleno ekonomem',
      'rejected': 'Zamítnuto',
      'rejected_by_economist': 'Zamítnuto ekonomem',
      'approved_by_director': 'Schváleno ředitelem',
      'rejected_by_director': 'Zamítnuto ředitelem',
      'accepted_by_referent': 'Přijato obchodním referentem',
      'ordered_by_referent': 'Objednáno obchodním referentem',
      'completed': 'Dokončeno',
      'rejected_by_referent': 'Zamítnuto obchodním referentem'
    }
    return labels[status] || status
  }

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'new':
        return 'status-new'
      case 'deferred':
        return 'status-deferred'
      case 'approved_by_economist':
      case 'approved_by_director':
        return 'status-approved'
      case 'rejected':
      case 'rejected_by_economist':
      case 'rejected_by_director':
      case 'rejected_by_referent':
        return 'status-rejected'
      case 'accepted_by_referent':
        return 'status-accepted'
      case 'ordered_by_referent':
        return 'status-ordered'
      case 'completed':
        return 'status-completed'
      default:
        return ''
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleString('cs-CZ', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handleTableAction = async (request: PurchaseRequest, action: string) => {
    if (action === 'accept') {
      await handleAccept(request.id)
    } else if (action === 'order') {
      await handleOrder(request.id)
    } else if (action === 'complete') {
      setSelectedRequest(request)
      setShowCompleteModal(true)
      setCompletionComment('')
    } else if (action === 'reject-referent') {
      openRejectModal(request)
    }
  }

  // Фильтрация заявок только по поисковому тексту (для статистики)
  const requestsForStats = useMemo(() => {
    if (!requests || requests.length === 0) return []
    if (!searchText.trim()) return requests
    
    const searchLower = searchText.toLowerCase().trim()
    return requests.filter(r => {
      const subject = (r.subject || '').toLowerCase()
      const description = (r.description || '').toLowerCase()
      const reason = (r.reason || '').toLowerCase()
      const supplier = (r.supplier || '').toLowerCase()
      const centerName = (r.center_name || '').toLowerCase()
      const createdByName = (r.created_by_name || '').toLowerCase()
      const notes = (r.notes || '').toLowerCase()
      
      return subject.includes(searchLower) ||
             description.includes(searchLower) ||
             reason.includes(searchLower) ||
             supplier.includes(searchLower) ||
             centerName.includes(searchLower) ||
             createdByName.includes(searchLower) ||
             notes.includes(searchLower)
    })
  }, [requests, searchText])

  // Фильтрация заявок по выбранному статусу и поисковому тексту (для отображения)
  const filteredRequests = useMemo(() => {
    let filtered = requestsForStats
    
    // Фильтр по статусу
    if (statusFilter) {
      filtered = filtered.filter(r => {
        if (statusFilter === 'approved_by_director') return r.status === 'approved_by_director'
        if (statusFilter === 'accepted_by_referent') return r.status === 'accepted_by_referent'
        if (statusFilter === 'ordered_by_referent') return r.status === 'ordered_by_referent'
        if (statusFilter === 'completed') return r.status === 'completed'
        if (statusFilter === 'rejected_by_referent') return r.status === 'rejected_by_referent'
        return true
      })
    }
    
    return filtered
  }, [requestsForStats, statusFilter])

  const approvedByDirectorRequests = useMemo(() => requestsForStats.filter(r => r.status === 'approved_by_director'), [requestsForStats])
  const acceptedByReferentRequests = useMemo(() => requestsForStats.filter(r => r.status === 'accepted_by_referent'), [requestsForStats])
  const orderedByReferentRequests = useMemo(() => requestsForStats.filter(r => r.status === 'ordered_by_referent'), [requestsForStats])
  const completedRequests = useMemo(() => requestsForStats.filter(r => r.status === 'completed'), [requestsForStats])
  const rejectedByReferentRequests = useMemo(() => requestsForStats.filter(r => r.status === 'rejected_by_referent'), [requestsForStats])

  // Функция для пагинации
  const getPaginatedRequests = (items: PurchaseRequest[], page: number) => {
    const startIndex = (page - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return items.slice(startIndex, endIndex)
  }

  const getTotalPages = (items: PurchaseRequest[]) => {
    return Math.ceil(items.length / itemsPerPage)
  }

  // Сбрасываем страницу при изменении фильтра или поиска
  useEffect(() => {
    setCurrentPage(1)
    setSectionPages({}) // Сбрасываем все страницы секций
  }, [statusFilter, searchText])

  const getSectionPage = (sectionKey: string) => {
    return sectionPages[sectionKey] || 1
  }

  const setSectionPage = (sectionKey: string, page: number) => {
    setSectionPages(prev => ({ ...prev, [sectionKey]: page }))
  }

  if (loading) {
    return <div className="referent-view">Načítání...</div>
  }

  if (printRequest) {
    return (
      <PurchaseRequestPrintView 
        request={printRequest}
        onBack={() => setPrintRequest(null)}
      />
    )
  }

  return (
    <div className="referent-view">
      <div className="view-controls">
        <button 
          className={viewMode === 'cards' ? 'active' : ''}
          onClick={() => setViewMode('cards')}
        >
          Karty
        </button>
        <button 
          className={viewMode === 'table' ? 'active' : ''}
          onClick={() => setViewMode('table')}
        >
          Tabulka
        </button>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="text"
            placeholder="Hledat podle textu..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '0.9rem',
              minWidth: '250px'
            }}
          />
          {searchText && (
            <button
              onClick={() => setSearchText('')}
              style={{
                padding: '0.5rem 1rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: '#f0f0f0',
                cursor: 'pointer'
              }}
            >
              Vymazat
            </button>
          )}
        </div>
      </div>

      <div className="admin-stats">
        <div 
          className={`stat-card ${statusFilter === 'approved_by_director' ? 'active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'approved_by_director' ? null : 'approved_by_director')}
          style={{ cursor: 'pointer' }}
        >
          <h3>Schváleno ředitelem</h3>
          <p className="stat-number">{approvedByDirectorRequests.length}</p>
        </div>
        <div 
          className={`stat-card ${statusFilter === 'accepted_by_referent' ? 'active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'accepted_by_referent' ? null : 'accepted_by_referent')}
          style={{ cursor: 'pointer' }}
        >
          <h3>Přijato obchodním referentem</h3>
          <p className="stat-number">{acceptedByReferentRequests.length}</p>
        </div>
        <div 
          className={`stat-card ${statusFilter === 'ordered_by_referent' ? 'active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'ordered_by_referent' ? null : 'ordered_by_referent')}
          style={{ cursor: 'pointer' }}
        >
          <h3>Objednáno obchodním referentem</h3>
          <p className="stat-number">{orderedByReferentRequests.length}</p>
        </div>
        <div 
          className={`stat-card ${statusFilter === 'completed' ? 'active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'completed' ? null : 'completed')}
          style={{ cursor: 'pointer' }}
        >
          <h3>Dokončeno</h3>
          <p className="stat-number">{completedRequests.length}</p>
        </div>
        <div 
          className={`stat-card ${statusFilter === 'rejected_by_referent' ? 'active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'rejected_by_referent' ? null : 'rejected_by_referent')}
          style={{ cursor: 'pointer' }}
        >
          <h3>Zamítnuto obchodním referentem</h3>
          <p className="stat-number">{rejectedByReferentRequests.length}</p>
        </div>
        <div 
          className={`stat-card ${statusFilter === null ? 'active' : ''}`}
          onClick={() => setStatusFilter(null)}
          style={{ cursor: 'pointer' }}
        >
          <h3>Celkem</h3>
          <p className="stat-number">{requestsForStats.length}</p>
        </div>
      </div>

      {viewMode === 'table' ? (
        <>
          <PurchaseRequestsTable 
            requests={filteredRequests}
            onAction={handleTableAction}
            onPrint={(request) => setPrintRequest(request)}
            filterType="referent"
            hideStatusFilter={true}
            statusFilter={statusFilter}
          />
          {filteredRequests.length > itemsPerPage && (
            <Pagination
              currentPage={currentPage}
              totalPages={getTotalPages(filteredRequests)}
              onPageChange={setCurrentPage}
              itemsPerPage={itemsPerPage}
              totalItems={filteredRequests.length}
            />
          )}
        </>
      ) : statusFilter ? (
        <div className="requests-container">
          {statusFilter === 'approved_by_director' && (
            <>
              <h2>Schváleno ředitelem ({approvedByDirectorRequests.length})</h2>
              {approvedByDirectorRequests.length > 0 ? (
                <>
                  <div className="requests-list">
                    {getPaginatedRequests(approvedByDirectorRequests, currentPage).map(request => (
                    <PurchaseRequestCard
                      key={request.id}
                      request={request}
                      getStatusLabel={getStatusLabel}
                      getStatusClass={getStatusClass}
                      searchText={searchText}
                      customActions={
                        <>
                          <button 
                            className="btn-print"
                            onClick={(e) => {
                              e.stopPropagation()
                              setPrintRequest(request)
                            }}
                          >
                            Tisk
                          </button>
                          <button 
                            className="btn-accept"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleAccept(request.id)
                            }}
                          >
                            Přijato
                          </button>
                        </>
                      }
                      onClick={() => {
                        setSelectedRequest(request)
                        setShowCardModal(true)
                      }}
                    />
                  ))}
                  </div>
                  {getTotalPages(approvedByDirectorRequests) > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={getTotalPages(approvedByDirectorRequests)}
                      onPageChange={setCurrentPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={approvedByDirectorRequests.length}
                    />
                  )}
                </>
              ) : (
                <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Žádné žádosti k přijetí</p>
              )}
            </>
          )}
          {statusFilter === 'accepted_by_referent' && (
            <>
              <h2>Přijato obchodním referentem ({acceptedByReferentRequests.length})</h2>
              {acceptedByReferentRequests.length > 0 ? (
                <>
                  <div className="requests-list">
                    {getPaginatedRequests(acceptedByReferentRequests, currentPage).map(request => (
                    <PurchaseRequestCard
                      key={request.id}
                      request={request}
                      getStatusLabel={getStatusLabel}
                      getStatusClass={getStatusClass}
                      searchText={searchText}
                      customActions={
                        <>
                          <button 
                            className="btn-print"
                            onClick={(e) => {
                              e.stopPropagation()
                              setPrintRequest(request)
                            }}
                          >
                            Tisk
                          </button>
                          <button 
                            className="btn-order"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleOrder(request.id)
                            }}
                          >
                            Objednáno
                          </button>
                          {renderRejectButton(request)}
                        </>
                      }
                      onClick={() => {
                        setSelectedRequest(request)
                        setShowCardModal(true)
                      }}
                    />
                  ))}
                  </div>
                  {getTotalPages(acceptedByReferentRequests) > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={getTotalPages(acceptedByReferentRequests)}
                      onPageChange={setCurrentPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={acceptedByReferentRequests.length}
                    />
                  )}
                </>
              ) : (
                <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Žádné přijaté žádosti</p>
              )}
            </>
          )}
          {statusFilter === 'ordered_by_referent' && (
            <>
              <h2>Objednáno obchodním referentem ({orderedByReferentRequests.length})</h2>
              {orderedByReferentRequests.length > 0 ? (
                <>
                  <div className="requests-list">
                    {getPaginatedRequests(orderedByReferentRequests, currentPage).map(request => (
                    <PurchaseRequestCard
                      key={request.id}
                      request={request}
                      getStatusLabel={getStatusLabel}
                      getStatusClass={getStatusClass}
                      searchText={searchText}
                      customActions={
                        <>
                          <button 
                            className="btn-print"
                            onClick={(e) => {
                              e.stopPropagation()
                              setPrintRequest(request)
                            }}
                          >
                            Tisk
                          </button>
                          <button 
                            className="btn-complete"
                            onClick={(e) => {
                              e.stopPropagation()
                              setSelectedRequest(request)
                              setCompletionComment('')
                              setShowCompleteModal(true)
                            }}
                          >
                            Hotovo
                          </button>
                          {renderRejectButton(request)}
                        </>
                      }
                      onClick={() => {
                        setSelectedRequest(request)
                        setShowCardModal(true)
                      }}
                    />
                  ))}
                  </div>
                  {getTotalPages(orderedByReferentRequests) > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={getTotalPages(orderedByReferentRequests)}
                      onPageChange={setCurrentPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={orderedByReferentRequests.length}
                    />
                  )}
                </>
              ) : (
                <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Žádné objednané žádosti</p>
              )}
            </>
          )}
          {statusFilter === 'rejected_by_referent' && (
            <>
              <h2>Zamítnuto obchodním referentem ({rejectedByReferentRequests.length})</h2>
              {rejectedByReferentRequests.length > 0 ? (
                <>
                  <div className="requests-list">
                    {getPaginatedRequests(rejectedByReferentRequests, currentPage).map(request => (
                    <PurchaseRequestCard
                      key={request.id}
                      request={request}
                      getStatusLabel={getStatusLabel}
                      getStatusClass={getStatusClass}
                      searchText={searchText}
                      customActions={
                        <button 
                          className="btn-print"
                          onClick={(e) => {
                            e.stopPropagation()
                            setPrintRequest(request)
                          }}
                        >
                          Tisk
                        </button>
                      }
                      onClick={() => {
                        setSelectedRequest(request)
                        setShowCardModal(true)
                      }}
                    />
                  ))}
                  </div>
                  {getTotalPages(rejectedByReferentRequests) > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={getTotalPages(rejectedByReferentRequests)}
                      onPageChange={setCurrentPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={rejectedByReferentRequests.length}
                    />
                  )}
                </>
              ) : (
                <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Žádné zamítnuté žádosti</p>
              )}
            </>
          )}
          {statusFilter === 'completed' && (
            <>
              <h2>Dokončené žádosti ({completedRequests.length})</h2>
              {completedRequests.length > 0 ? (
                <>
                  <div className="requests-list">
                    {getPaginatedRequests(completedRequests, currentPage).map(request => (
                    <PurchaseRequestCard
                      key={request.id}
                      request={request}
                      getStatusLabel={getStatusLabel}
                      getStatusClass={getStatusClass}
                      searchText={searchText}
                      customActions={
                        <button 
                          className="btn-print"
                          onClick={(e) => {
                            e.stopPropagation()
                            setPrintRequest(request)
                          }}
                        >
                          Tisk
                        </button>
                      }
                      onClick={() => {
                        setSelectedRequest(request)
                        setShowCardModal(true)
                      }}
                    />
                  ))}
                  </div>
                  {getTotalPages(completedRequests) > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={getTotalPages(completedRequests)}
                      onPageChange={setCurrentPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={completedRequests.length}
                    />
                  )}
                </>
              ) : (
                <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Žádné dokončené žádosti</p>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="requests-container">
        <h2>Schválené ředitelem ({approvedByDirectorRequests.length})</h2>
        {approvedByDirectorRequests.length > 0 ? (
          <>
            <div className="requests-list">
              {getPaginatedRequests(approvedByDirectorRequests, getSectionPage('approved_director')).map(request => (
              <PurchaseRequestCard
                key={request.id}
                request={request}
                getStatusLabel={getStatusLabel}
                getStatusClass={getStatusClass}
                customActions={
                  <>
                    <button 
                      className="btn-print"
                      onClick={(e) => {
                        e.stopPropagation()
                        setPrintRequest(request)
                      }}
                    >
                      Tisk
                    </button>
                    <button 
                      className="btn-accept"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleAccept(request.id)
                      }}
                    >
                      Přijato
                    </button>
                  </>
                }
                onClick={() => {
                  setSelectedRequest(request)
                  setShowCardModal(true)
                }}
              />
              ))}
            </div>
            {getTotalPages(approvedByDirectorRequests) > 1 && (
              <Pagination
                currentPage={getSectionPage('approved_director')}
                totalPages={getTotalPages(approvedByDirectorRequests)}
                onPageChange={(page) => setSectionPage('approved_director', page)}
                itemsPerPage={itemsPerPage}
                totalItems={approvedByDirectorRequests.length}
              />
            )}
          </>
        ) : (
          <p style={{ padding: '1rem', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>Žádné žádosti k přijetí</p>
        )}

        <h2>Přijaté žádosti ({acceptedByReferentRequests.length})</h2>
        {acceptedByReferentRequests.length > 0 ? (
          <>
            <div className="requests-list">
              {getPaginatedRequests(acceptedByReferentRequests, getSectionPage('accepted_referent')).map(request => (
              <PurchaseRequestCard
                key={request.id}
                request={request}
                getStatusLabel={getStatusLabel}
                getStatusClass={getStatusClass}
                customActions={
                  <>
                    <button 
                      className="btn-print"
                      onClick={(e) => {
                        e.stopPropagation()
                        setPrintRequest(request)
                      }}
                    >
                      Tisk
                    </button>
                    <button 
                      className="btn-order"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleOrder(request.id)
                      }}
                    >
                      Objednáno
                    </button>
                    {renderRejectButton(request)}
                  </>
                }
                onClick={() => {
                  setSelectedRequest(request)
                  setShowCardModal(true)
                }}
              />
              ))}
            </div>
            {getTotalPages(acceptedByReferentRequests) > 1 && (
              <Pagination
                currentPage={getSectionPage('accepted_referent')}
                totalPages={getTotalPages(acceptedByReferentRequests)}
                onPageChange={(page) => setSectionPage('accepted_referent', page)}
                itemsPerPage={itemsPerPage}
                totalItems={acceptedByReferentRequests.length}
              />
            )}
          </>
        ) : (
          <p style={{ padding: '1rem', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>Žádné přijaté žádosti</p>
        )}

        <h2>Objednané žádosti ({orderedByReferentRequests.length})</h2>
        {orderedByReferentRequests.length > 0 ? (
          <>
            <div className="requests-list">
              {getPaginatedRequests(orderedByReferentRequests, getSectionPage('ordered_referent')).map(request => (
              <PurchaseRequestCard
                key={request.id}
                request={request}
                getStatusLabel={getStatusLabel}
                getStatusClass={getStatusClass}
                customActions={
                  <>
                    <button 
                      className="btn-print"
                      onClick={(e) => {
                        e.stopPropagation()
                        setPrintRequest(request)
                      }}
                    >
                      Tisk
                    </button>
                    <button 
                      className="btn-complete"
                      onClick={(e) => {
                        e.stopPropagation()
                        setSelectedRequest(request)
                        setCompletionComment('')
                        setShowCompleteModal(true)
                      }}
                    >
                      Hotovo
                    </button>
                    {renderRejectButton(request)}
                  </>
                }
                onClick={() => {
                  setSelectedRequest(request)
                  setShowCardModal(true)
                }}
              />
              ))}
            </div>
            {getTotalPages(orderedByReferentRequests) > 1 && (
              <Pagination
                currentPage={getSectionPage('ordered_referent')}
                totalPages={getTotalPages(orderedByReferentRequests)}
                onPageChange={(page) => setSectionPage('ordered_referent', page)}
                itemsPerPage={itemsPerPage}
                totalItems={orderedByReferentRequests.length}
              />
            )}
          </>
        ) : (
          <p style={{ padding: '1rem', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>Žádné objednané žádosti</p>
        )}

        <h2>Zamítnuté žádosti ({rejectedByReferentRequests.length})</h2>
        {rejectedByReferentRequests.length > 0 ? (
          <>
            <div className="requests-list">
              {getPaginatedRequests(rejectedByReferentRequests, getSectionPage('rejected_referent')).map(request => (
              <PurchaseRequestCard
                key={request.id}
                request={request}
                getStatusLabel={getStatusLabel}
                getStatusClass={getStatusClass}
                searchText={searchText}
                customActions={
                  <button 
                    className="btn-print"
                    onClick={(e) => {
                      e.stopPropagation()
                      setPrintRequest(request)
                    }}
                  >
                    Tisk
                  </button>
                }
                onClick={() => {
                  setSelectedRequest(request)
                  setShowCardModal(true)
                }}
              />
              ))}
            </div>
            {getTotalPages(rejectedByReferentRequests) > 1 && (
              <Pagination
                currentPage={getSectionPage('rejected_referent')}
                totalPages={getTotalPages(rejectedByReferentRequests)}
                onPageChange={(page) => setSectionPage('rejected_referent', page)}
                itemsPerPage={itemsPerPage}
                totalItems={rejectedByReferentRequests.length}
              />
            )}
          </>
        ) : (
          <p style={{ padding: '1rem', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>Žádné zamítnuté žádosti</p>
        )}

        <h2>Dokončené žádosti ({completedRequests.length})</h2>
        {completedRequests.length > 0 ? (
          <>
            <div className="requests-list">
              {getPaginatedRequests(completedRequests, getSectionPage('completed')).map(request => (
              <PurchaseRequestCard
                key={request.id}
                request={request}
                getStatusLabel={getStatusLabel}
                getStatusClass={getStatusClass}
                customActions={
                  <button 
                    className="btn-print"
                    onClick={(e) => {
                      e.stopPropagation()
                      setPrintRequest(request)
                    }}
                  >
                    Tisk
                  </button>
                }
                onClick={() => {
                  setSelectedRequest(request)
                  setShowCardModal(true)
                }}
              />
              ))}
            </div>
            {getTotalPages(completedRequests) > 1 && (
              <Pagination
                currentPage={getSectionPage('completed')}
                totalPages={getTotalPages(completedRequests)}
                onPageChange={(page) => setSectionPage('completed', page)}
                itemsPerPage={itemsPerPage}
                totalItems={completedRequests.length}
              />
            )}
          </>
        ) : (
          <p style={{ padding: '1rem', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>Žádné dokončené žádosti</p>
        )}
      </div>
      )}

      {showCompleteModal && selectedRequest && (
        <div className="modal-overlay" onClick={() => {
          setShowCompleteModal(false)
          setSelectedRequest(null)
          setCompletionComment('')
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Dokončit žádost</h3>
            <div className="modal-request-info">
              <p><strong>Předmět:</strong> {selectedRequest.subject}</p>
              <p><strong>Středisko:</strong> {selectedRequest.center_name}</p>
            </div>
            <div className="modal-form">
              <label>
                Komentář (volitelné):
                <textarea
                  value={completionComment}
                  onChange={(e) => setCompletionComment(e.target.value)}
                  rows={4}
                  placeholder="Zadejte komentář..."
                />
              </label>
              <div className="modal-actions">
                <button 
                  className="btn-complete"
                  onClick={handleComplete}
                >
                  Dokončit
                </button>
                <button 
                  className="btn-cancel"
                  onClick={() => {
                    setShowCompleteModal(false)
                    setSelectedRequest(null)
                    setCompletionComment('')
                  }}
                >
                  Zrušit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCardModal && selectedRequest && (
        <DetailsModal
          isOpen={showCardModal}
          onClose={() => {
            setShowCardModal(false)
            setSelectedRequest(null)
          }}
          title={`Žádost #${selectedRequest.id.substring(0, 8)}`}
          actions={
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button 
                className="btn-print-small" 
                onClick={() => {
                  setPrintRequest(selectedRequest)
                  setShowCardModal(false)
                  setSelectedRequest(null)
                }}
              >
                🖨️ Tisk
              </button>
              {selectedRequest.status === 'approved_by_director' && (
                <button 
                  className="btn-accept-small" 
                  onClick={() => {
                    handleAccept(selectedRequest.id)
                    setShowCardModal(false)
                    setSelectedRequest(null)
                  }}
                >
                  Přijato
                </button>
              )}
              {selectedRequest.status === 'accepted_by_referent' && (
                <>
                  <button 
                    className="btn-order-small" 
                    onClick={() => {
                      handleOrder(selectedRequest.id)
                      setShowCardModal(false)
                      setSelectedRequest(null)
                    }}
                  >
                    Objednáno
                  </button>
                  <button 
                    className="btn-reject-small" 
                    onClick={() => openRejectModal(selectedRequest)}
                  >
                    Zamítnout
                  </button>
                </>
              )}
              {selectedRequest.status === 'ordered_by_referent' && (
                <>
                  <button 
                    className="btn-complete-small" 
                    onClick={() => {
                      setShowCardModal(false)
                      setShowCompleteModal(true)
                      setCompletionComment('')
                    }}
                  >
                    Hotovo
                  </button>
                  <button 
                    className="btn-reject-small" 
                    onClick={() => openRejectModal(selectedRequest)}
                  >
                    Zamítnout
                  </button>
                </>
              )}
            </div>
          }
        >
          <div className="details-section">
            <div className="details-section-title">Základní informace</div>
            <div className="details-grid">
              <div className="details-item">
                <div className="details-label">Předmět</div>
                <div className="details-value"><strong>{selectedRequest.subject}</strong></div>
              </div>
              <div className="details-item">
                <div className="details-label">Status</div>
                <div className="details-value">
                  <span className={`status-badge ${getStatusClass(selectedRequest.status)}`}>
                    {getStatusLabel(selectedRequest.status)}
                  </span>
                </div>
              </div>
              <div className="details-item">
                <div className="details-label">Středisko</div>
                <div className="details-value">{selectedRequest.center_name || '-'}</div>
              </div>
              <div className="details-item">
                <div className="details-label">Částka</div>
                <div className="details-value">
                  {selectedRequest.amount ? `${selectedRequest.amount.toLocaleString('cs-CZ')} Kč` : '-'}
                </div>
              </div>
            </div>
          </div>
          <div className="details-section">
            <div className="details-section-title">Kontaktní informace</div>
            <div className="details-grid">
              <div className="details-item">
                <div className="details-label">Odpovědná osoba</div>
                <div className="details-value"><strong>{selectedRequest.created_by_name || '-'}</strong></div>
              </div>
              <div className="details-item">
                <div className="details-label">E-Mail</div>
                <div className="details-value">
                  {selectedRequest.created_by_email ? (
                    <a href={`mailto:${selectedRequest.created_by_email}`} className="email-link">
                      {selectedRequest.created_by_email}
                    </a>
                  ) : '-'}
                </div>
              </div>
              <div className="details-item">
                <div className="details-label">Telefon</div>
                <div className="details-value">
                  {selectedRequest.phone ? (
                    <a href={`tel:${selectedRequest.phone}`} className="phone-link">
                      {selectedRequest.phone}
                    </a>
                  ) : '-'}
                </div>
              </div>
            </div>
          </div>
          <div className="details-section">
            <div className="details-section-title">Popis</div>
            <div className="details-value" style={{ whiteSpace: 'pre-wrap' }}>
              {selectedRequest.description || '-'}
            </div>
          </div>
          <div className="details-section">
            <div className="details-section-title">Důvod objednání</div>
            <div className="details-value" style={{ whiteSpace: 'pre-wrap' }}>
              {selectedRequest.reason || '-'}
            </div>
          </div>
          {selectedRequest.supplier && (
            <div className="details-section">
              <div className="details-section-title">Dodavatel</div>
              <div className="details-value">{selectedRequest.supplier}</div>
            </div>
          )}
          {selectedRequest.file_name && selectedRequest.file_url && (
            <div className="details-section">
              <div className="details-section-title">Příloha</div>
              <div className="details-value">
                <a
                  href={protectedFileHref(selectedRequest.file_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="file-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  📎 {selectedRequest.file_name}
                </a>
                <span style={{ marginLeft: '10px', fontSize: '0.9em' }}>
                  <a
                    href={protectedFileHref(selectedRequest.file_url)}
                    download={selectedRequest.file_name}
                    className="file-link"
                    title="Stáhnout soubor"
                    onClick={(e) => e.stopPropagation()}
                  >
                    ⬇️ Stáhnout
                  </a>
                </span>
              </div>
            </div>
          )}
          {selectedRequest.notes && (
            <div className="details-section">
              <div className="details-section-title">Poznámky</div>
              <div className="details-value" style={{ whiteSpace: 'pre-wrap' }}>
                {selectedRequest.notes}
              </div>
            </div>
          )}
          <div className="details-section">
            <div className="details-section-title">Časové údaje</div>
            <div className="details-grid">
              <div className="details-item">
                <div className="details-label">Datum vytvoření</div>
                <div className="details-value">{formatDate(selectedRequest.created_at)}</div>
              </div>
              {selectedRequest.approved_by_economist_at && (
                <div className="details-item">
                  <div className="details-label">Schváleno ekonomem</div>
                  <div className="details-value">{formatDate(selectedRequest.approved_by_economist_at)}</div>
                </div>
              )}
              {selectedRequest.deferred_at && (
                <div className="details-item">
                  <div className="details-label">Odloženo ekonomem</div>
                  <div className="details-value">{formatDate(selectedRequest.deferred_at)}</div>
                </div>
              )}
              {selectedRequest.rejected_by_economist_at && (
                <div className="details-item">
                  <div className="details-label">Zamítnuto ekonomem</div>
                  <div className="details-value">{formatDate(selectedRequest.rejected_by_economist_at)}</div>
                </div>
              )}
              {selectedRequest.approved_by_director_at && (
                <div className="details-item">
                  <div className="details-label">Schváleno ředitelem</div>
                  <div className="details-value">{formatDate(selectedRequest.approved_by_director_at)}</div>
                </div>
              )}
              {selectedRequest.rejected_by_director_at && (
                <div className="details-item">
                  <div className="details-label">Zamítnuto ředitelem</div>
                  <div className="details-value">{formatDate(selectedRequest.rejected_by_director_at)}</div>
                </div>
              )}
              {selectedRequest.accepted_by_referent_at && (
                <div className="details-item">
                  <div className="details-label">Přijato referentem</div>
                  <div className="details-value">{formatDate(selectedRequest.accepted_by_referent_at)}</div>
                </div>
              )}
              {selectedRequest.ordered_by_referent_at && (
                <div className="details-item">
                  <div className="details-label">Objednáno referentem</div>
                  <div className="details-value">{formatDate(selectedRequest.ordered_by_referent_at)}</div>
                </div>
              )}
              {selectedRequest.ready_by_referent_at && (
                <div className="details-item">
                  <div className="details-label">Připraveno referentem</div>
                  <div className="details-value">{formatDate(selectedRequest.ready_by_referent_at)}</div>
                </div>
              )}
              {selectedRequest.rejected_by_referent_at && (
                <div className="details-item">
                  <div className="details-label">Zamítnuto referentem</div>
                  <div className="details-value">
                    {selectedRequest.rejected_by_referent_name ? `${selectedRequest.rejected_by_referent_name}, ` : ''}
                    {formatDate(selectedRequest.rejected_by_referent_at)}
                  </div>
                </div>
              )}
              {selectedRequest.rejected_by_referent_reason && (
                <div className="details-item">
                  <div className="details-label">Důvod zamítnutí</div>
                  <div className="details-value" style={{ whiteSpace: 'pre-wrap' }}>
                    {selectedRequest.rejected_by_referent_reason}
                  </div>
                </div>
              )}
              {selectedRequest.completed_at && (
                <div className="details-item">
                  <div className="details-label">Dokončeno</div>
                  <div className="details-value">{formatDate(selectedRequest.completed_at)}</div>
                </div>
              )}
            </div>
          </div>
        </DetailsModal>
      )}

      <CancelRequestModal
        isOpen={showRejectModal}
        onClose={() => {
          setShowRejectModal(false)
          setRejectModalRequest(null)
        }}
        onConfirm={handleRejectReferent}
        requestSubject={rejectModalRequest?.subject}
        title="Zamítnout žádost"
        warning='Opravdu chcete zamítnout tuto žádost? Status se změní na "Zamítnuto obchodním referentem" a žadatel obdrží e-mail s uvedeným důvodem.'
        reasonLabel="Důvod zamítnutí"
        reasonPlaceholder="Zadejte důvod zamítnutí žádosti (minimálně 10 znaků)"
        confirmLabel="Potvrdit zamítnutí"
        emptyReasonError="Prosím zadejte důvod zamítnutí žádosti"
      />
    </div>
  )
}

export default ReferentView

